import { useEffect } from 'react';
import { ApolloError, useQuery } from '@apollo/client';

import { useSnackbar } from '../providers/snackbar/hook/useSnackbar';
import {
  GET_FACEBOOK_METRICS,
  GET_GOOGLE_ADS_METRICS,
  GET_GOOGLE_ANALYTICS_METRICS,
  GET_GOOGLE_SEARCH_CONSOLE_METRICS,
  GET_MAGENTO_METRICS,
  GET_SHOPIFY_METRICS,
} from '../utils/gql/queries';
import { Query } from '../utils/gql/types';

interface UseMetricsDataProps {
  facebookConnectorId?: string;
  googleAdsConnectorId?: string;
  googleAnalytics4ConnectorId?: string;
  googleSearchConsoleConnectorId?: string;
  shopifyConnectorId?: string;
  magentoConnectorId?: string;
  magentoConnectorEnabled?: boolean;
}

const useBackendMetricsData = ({
  facebookConnectorId,
  googleAdsConnectorId,
  googleAnalytics4ConnectorId,
  googleSearchConsoleConnectorId,
  shopifyConnectorId,
  magentoConnectorId,
  magentoConnectorEnabled = false,
}: UseMetricsDataProps) => {
  const snackbar = useSnackbar();

  const onError = (err: ApolloError) => {
    const { graphQLErrors, message: errorText } = err;
    const message =
      graphQLErrors && graphQLErrors.length > 0
        ? graphQLErrors[0].message
        : errorText;

    snackbar(message);
  };

  const {
    data: facebookMetricsData,
    loading: gettingFacebookMetrics,
    error: facebookError,
  } = useQuery<Pick<Query, 'getFacebookMetrics'>, never>(GET_FACEBOOK_METRICS, {
    skip: !facebookConnectorId,
    // Evita cache stale quando aggiungiamo nuove metriche lato backend.
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const {
    data: googleAdsMetricsData,
    loading: gettingGoogleAdsMetrics,
    error: googleAdsError,
  } = useQuery<Pick<Query, 'getGoogleAdsMetricsList'>, never>(
    GET_GOOGLE_ADS_METRICS,
    {
      skip: !googleAdsConnectorId,
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    },
  );

  const {
    data: googleAnalyticsMetricsData,
    loading: gettingGoogleAnalyticsMetrics,
    error: analyticsError,
  } = useQuery<Pick<Query, 'getGoogleAnalyticsMetrics'>, never>(
    GET_GOOGLE_ANALYTICS_METRICS,
    {
      skip: !googleAnalytics4ConnectorId,
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    },
  );

  const {
    data: googleSearchConsoleMetricsData,
    loading: gettingGoogleSearchConsoleMetrics,
    error: searchConsoleError,
  } = useQuery<Pick<Query, 'getGoogleSearchConsoleMetrics'>, never>(
    GET_GOOGLE_SEARCH_CONSOLE_METRICS,
    {
      skip: !googleSearchConsoleConnectorId,
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    },
  );

  const {
    data: shopifyMetricsData,
    loading: gettingShopifyMetrics,
    error: shopifyError,
  } = useQuery<Pick<Query, 'getShopifyMetrics'>, never>(GET_SHOPIFY_METRICS, {
    skip: !shopifyConnectorId,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const {
    data: magentoMetricsData,
    loading: gettingMagentoMetrics,
    error: magentoError,
  } = useQuery<Pick<Query, 'getMagentoMetrics'>, never>(GET_MAGENTO_METRICS, {
    skip: !magentoConnectorEnabled || !magentoConnectorId,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  // Handle Facebook error
  useEffect(() => {
    if (facebookError) {
      onError(facebookError);
    }
  }, [facebookError, onError]);

  // Handle Google Ads error
  useEffect(() => {
    if (googleAdsError) {
      onError(googleAdsError);
    }
  }, [googleAdsError, onError]);

  // Handle Analytics error
  useEffect(() => {
    if (analyticsError) {
      onError(analyticsError);
    }
  }, [analyticsError, onError]);

  useEffect(() => {
    if (searchConsoleError) {
      onError(searchConsoleError);
    }
  }, [searchConsoleError, onError]);

  // Handle Shopify error
  useEffect(() => {
    if (shopifyError) {
      onError(shopifyError);
    }
  }, [shopifyError, onError]);

  useEffect(() => {
    if (magentoError) {
      onError(magentoError);
    }
  }, [magentoError, onError]);

  return {
    shopifyMetrics: (
      shopifyMetricsData?.getShopifyMetrics?.metrics ?? []
    ).filter((metric) => metric.label !== 'Customers'),
    magentoMetrics: magentoMetricsData?.getMagentoMetrics?.metrics ?? [],
    facebookMetrics: facebookMetricsData?.getFacebookMetrics?.metrics ?? [],
    googleAdsMetrics:
      googleAdsMetricsData?.getGoogleAdsMetricsList?.metrics ?? [],
    googleAnalyticsMetrics:
      googleAnalyticsMetricsData?.getGoogleAnalyticsMetrics?.metrics ?? [],
    googleSearchConsoleMetrics:
      googleSearchConsoleMetricsData?.getGoogleSearchConsoleMetrics?.metrics ??
      [],
    gettingFacebookMetrics,
    gettingShopifyMetrics,
    gettingMagentoMetrics,
    gettingGoogleAdsMetrics,
    gettingGoogleAnalyticsMetrics,
    gettingGoogleSearchConsoleMetrics,
    ready: !(
      gettingFacebookMetrics ||
      gettingShopifyMetrics ||
      gettingMagentoMetrics ||
      gettingGoogleAdsMetrics ||
      gettingGoogleAnalyticsMetrics ||
      gettingGoogleSearchConsoleMetrics
    ),
  };
};

export default useBackendMetricsData;
